import { BEIGE_600, BOX_SHADOW_XS, GRAY_800 } from 'constants/styling/theme'
import { Box, ClickAwayListener, Grow, MenuList, Paper, PopperPlacementType, SxProps, listClasses, styled } from '@mui/material'
import React, { FocusEvent, Fragment, KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import { MUIButton } from '../MUIButton/MUIButton.component'
import { MUIDropdownKeyboardContext } from './MUIDropdownKeyboard.context'
import { MUIDropdownPopper } from '../MUIDropdownPopper'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Dropdown id */
  id?: string
  /** Dropdown placement */
  placement?: PopperPlacementType
  /** Whether the dropdown is in dark mode */
  isDarkMode?: boolean
  /** Custom dropdown sx styles */
  sx?: SxProps
  /** Custom dropdown button */
  button?: (isOpen: boolean, action: (e: MouseEvent<HTMLElement>) => void, onClose: () => void) => ReactNode
  /** Dropdown children */
  children: ReactNode
  /** External control for open state */
  open?: boolean
  /** External control for onClose callback */
  onClose?: () => void
  /** External control for onBlur callback */
  onBlur?: () => void
  /** Whether to disable portal rendering (default true). Set to false when used inside overflow-hidden containers like modals. */
  disablePortal?: boolean
  /**
   * Menu keyboard behaviour: opening focuses the first item, the arrow keys move through the items without closing
   * the list, and Escape or a pick closes it and returns focus to the trigger. The Escape is marked as used
   * (`defaultPrevented`), so an accessible `Modal` around it stays open.
   */
  isKeyboardNavigable?: boolean
}

/** MUI's MenuItem runs its onClick on Enter (keydown) and Space (keyup) without a click event reaching the list. */
const isItemPickKey = (e: KeyboardEvent<HTMLUListElement>, key: string) => e.key === key
  && (e.target as HTMLElement).getAttribute('role') === 'menuitem'
  && (e.target as HTMLElement).getAttribute('aria-disabled') !== 'true'

/** A link item gets its click from the browser on Enter: closing the list on keydown would move focus before it. */
const isLinkItem = (e: KeyboardEvent<HTMLUListElement>) => (e.target as HTMLElement).matches('a[href]')

const StyledMenuList = styled(MenuList)({
  [`&.${listClasses.root}`]: {
    padding: '0',
  },
})

/**
 * @component MUI Dropdown
 * @example
 * <MUIDropdown />
 */
export const MUIDropdown: React.FC<Props> = ({
  id,
  placement = 'bottom-start',
  isDarkMode = false,
  sx,
  button = (isOpen, action, onClose) => (
    <MUIButton isIconButton size="sm" type="secondaryNoBorder" onClick={action}>
      {isOpen ?
        <KeyboardArrowUpRoundedIcon fontSize="large" /> :
        <KeyboardArrowDownRoundedIcon fontSize="large" />
      }
    </MUIButton>
  ),
  children,
  open: externalOpen,
  onClose: externalOnClose,
  onBlur,
  disablePortal = true,
  isKeyboardNavigable = false,
}) => {
  const buttonAnchorRef = useRef<HTMLAnchorElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const isReturningFocusRef = useRef(false)
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined)

  // Use external control if provided, otherwise use internal state
  const isOpen = externalOpen !== undefined ? externalOpen : internalIsOpen

  // Measure anchor width when dropdown opens
  useEffect(() => {
    if (isOpen && buttonAnchorRef.current) {
      const width = buttonAnchorRef.current.offsetWidth
      setAnchorWidth(width)
    }
  }, [isOpen])

  const onOpenDropdown = (e: MouseEvent<HTMLElement>) => {
    if (externalOpen === undefined) {
      setInternalIsOpen(true)
    }
    e.stopPropagation()
  }

  const onClose = useCallback(() => {
    if (externalOpen === undefined) {
      setInternalIsOpen(false)
    }
    externalOnClose?.()
  }, [externalOpen, externalOnClose])

  /** Moves focus back to the trigger without the move itself counting as leaving the list. */
  const returnFocusToTrigger = useCallback(() => {
    const trigger = buttonAnchorRef.current?.querySelector<HTMLElement>('button, input, a[href], [tabindex]:not([tabindex="-1"])')
    isReturningFocusRef.current = true
    trigger?.focus()
    isReturningFocusRef.current = false
  }, [])

  const onPopperBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (isKeyboardNavigable) {
      // Focus moving between items, or back to the trigger on a close already under way, is not leaving the list
      if (isReturningFocusRef.current || paperRef.current?.contains(e.relatedTarget as Node | null)) return
    }
    if (onBlur) onBlur()
    else onClose()
  }, [isKeyboardNavigable, onBlur, onClose])

  const onListKeyDown = useCallback((e: KeyboardEvent<HTMLUListElement>) => {
    // Space on a link would scroll the list before its keyup click
    if (isItemPickKey(e, ' ') && isLinkItem(e)) return e.preventDefault()
    if (e.key === 'Escape' && !e.defaultPrevented) e.preventDefault()
    else if (!isItemPickKey(e, 'Enter') || isLinkItem(e)) return
    returnFocusToTrigger()
    onClose()
  }, [onClose, returnFocusToTrigger])

  const onListKeyUp = useCallback((e: KeyboardEvent<HTMLUListElement>) => {
    if (!isItemPickKey(e, ' ')) return
    // Space does not follow a link by itself; its click closes the list through onListClick
    if (isLinkItem(e)) return (e.target as HTMLElement).click()
    returnFocusToTrigger()
    onClose()
  }, [onClose, returnFocusToTrigger])

  const onListClick = useCallback(() => {
    if (paperRef.current?.contains(document.activeElement)) returnFocusToTrigger()
    onClose()
  }, [onClose, returnFocusToTrigger])

  return (
    <Fragment>
      <Box ref={buttonAnchorRef}>
        {button(isOpen, onOpenDropdown, onClose)}
      </Box>

      <MUIDropdownPopper
        id={id}
        anchorEl={buttonAnchorRef.current}
        open={isOpen}
        onBlur={isKeyboardNavigable ? onPopperBlur : (onBlur ? onBlur : onClose)}
        placement={placement}
        transition
        disablePortal={disablePortal}
        sx={sx}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'left top',
            }}
          >
            <Paper
              ref={paperRef}
              sx={{
                overflow: 'auto',
                boxSizing: 'border-box',
                boxShadow: BOX_SHADOW_XS,
                border: isDarkMode ? `1px solid ${GRAY_800}` : `1px solid ${BEIGE_600}`,
                minWidth: anchorWidth ? `${anchorWidth}px` : 'auto',
              }}
              elevation={0}
              onClick={!disablePortal ? (e) => e.stopPropagation() : undefined}
            >
              <ClickAwayListener onClickAway={onClose}>
                {/* Around the list, not inside it: MenuList hands focus props to its direct children */}
                <MUIDropdownKeyboardContext.Provider value={isKeyboardNavigable}>
                  <StyledMenuList
                    autoFocusItem={isOpen}
                    onClick={isKeyboardNavigable ? onListClick : onClose}
                    onKeyDown={isKeyboardNavigable ? onListKeyDown : undefined}
                    onKeyUp={isKeyboardNavigable ? onListKeyUp : undefined}
                  >
                    {children}
                  </StyledMenuList>
                </MUIDropdownKeyboardContext.Provider>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </MUIDropdownPopper>

    </Fragment>
  )
}
